import React from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import PageLoader from "components/loaders/PageLoader";
import {
  AdminPermission,
  getDefaultAuthorizedRoute,
  hasAllPermissions,
  hasAnyPermission,
  hasPermission,
  normalizePermissionSubject,
} from "helpers/auth/rbac";
import { clearAuthStorage, getToken, getUser, setToken, setUser } from "helpers/storage";
import { UserType } from "helpers/types/user.types";

type LoginDataTypes = {
  token: string;
  user: UserType;
};

interface AuthContextType {
  user: UserType | null;
  isAuthenticated: boolean;
  signin: (loginData: LoginDataTypes) => void;
  signout: () => void;
  can: (permission?: AdminPermission | null) => boolean;
  canAny: (permissions?: AdminPermission[] | null) => boolean;
  canAll: (permissions?: AdminPermission[] | null) => boolean;
}
const AuthContext = React.createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  signin: () => {},
  signout: () => {},
  can: () => false,
  canAny: () => false,
  canAll: () => false,
});

function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [user, setUserState] = React.useState<UserType | null>(null);
  const [isBootstaping, setIsBootstraping] = React.useState(true);

  React.useEffect(() => {
    const token = getToken();
    const storedUser = normalizePermissionSubject(getUser());

    if (!token) {
      clearAuthStorage();
      setUserState(null);
      setIsBootstraping(false);
      return;
    }

    if (!storedUser) {
      clearAuthStorage();
      setUserState(null);
      setIsBootstraping(false);
      return;
    }

    setUserState(storedUser);
    setIsBootstraping(false);
  }, []);

  const signin = React.useCallback(
    async (loginData: LoginDataTypes) => {
      try {
        const normalizedUser = normalizePermissionSubject(loginData?.user);

        if (!normalizedUser) {
          throw new Error("Invalid user permissions received.");
        }

        setToken(loginData?.token);
        setUser(normalizedUser);
        setUserState(normalizedUser);
        navigate(getDefaultAuthorizedRoute(normalizedUser), { replace: true });
      } catch (err) {
        console.log(err);
      }
    },
    [navigate]
  );

  const signout = React.useCallback(() => {
    clearAuthStorage();
    setUserState(null);
    queryClient.clear();
    navigate("/login", { replace: true });
  }, [navigate, queryClient]);

  const value = React.useMemo(
    () => ({
      user,
      isAuthenticated: Boolean(user),
      signin,
      signout,
      can: (permission?: AdminPermission | null) => hasPermission(user, permission),
      canAny: (permissions?: AdminPermission[] | null) =>
        hasAnyPermission(user, permissions),
      canAll: (permissions?: AdminPermission[] | null) =>
        hasAllPermissions(user, permissions),
    }),
    [signin, signout, user]
  );

  if (isBootstaping) {
    return <PageLoader />;
  }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function useAuth() {
  return React.useContext(AuthContext);
}

export { AuthProvider, useAuth };
